import { useEffect, useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { Theme } from "@radix-ui/themes";
import type { Session } from "@supabase/supabase-js";

import { supabase } from "./lib/supabase";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { AnonymousProvider } from "./context/AnonymousContext";
import SignIn from "./pages/SignIn";
import AuthCallback from "./pages/AuthCallback";
import Dashboard from "./pages/Dashboard";
import Flow360Browser from "./pages/Flow360Browser";
import FixSessionPage from "./pages/FixSessionPage";
import Project from "./pages/Project";
import Settings from "./pages/Settings";
import ShareView from "./pages/ShareView";
import QRScan from "./pages/QRScan";
import CLIAuthPage from "./pages/CLIAuthPage";
import Presentation from "./pages/Presentation";
import Embed from "./pages/Embed";
import BranchingStory from "./pages/BranchingStory";
import { CampaignConfigurator } from "./features/adaptive-sampling/configurator";
import { CampaignView } from "./features/adaptive-sampling/views";

import "@radix-ui/themes/styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

function CLIPortRedirect() {
  const [params] = useSearchParams();
  const cliPort = params.get("cli_port");
  if (cliPort) return <Navigate to={`/auth/cli?port=${cliPort}`} replace />;
  return null;
}

function AppRoutes() {
  const { theme } = useTheme();
  const [session, setSession] = useState<Session | null>(null);
  const isLocalDev = location.hostname === "localhost" || location.hostname === "127.0.0.1";
  const hasE2EToken =
    typeof window !== "undefined" && !!window.localStorage.getItem("thread_e2e_token");
  const [flow360Auth, setFlow360Auth] = useState(isLocalDev);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      if (!s && (isLocalDev || sessionStorage.getItem("flow360_access_token"))) {
        setFlow360Auth(true);
      }
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      if (!s && sessionStorage.getItem("flow360_access_token")) {
        setFlow360Auth(true);
      }
    });

    const handleStorage = () => {
      if (sessionStorage.getItem("flow360_access_token")) {
        setFlow360Auth(true);
      }
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      subscription.unsubscribe();
      window.removeEventListener("storage", handleStorage);
    };
  }, []);

  if (loading) {
    return (
      <Theme appearance={theme} accentColor="blue" radius="medium">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
          }}
        >
          <p style={{ color: "var(--gray-9)" }}>Loading...</p>
        </div>
      </Theme>
    );
  }

  return (
    <Theme appearance={theme} accentColor="blue" radius="medium">
      <CLIPortRedirect />
      <Routes>
        <Route path="/share/:shareId" element={<ShareView />} />
        <Route path="/embed/:projectId/:view" element={<Embed />} />
        <Route path="/qr" element={<QRScan />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/cli" element={<CLIAuthPage />} />
        <Route path="/presentations/:slug" element={<Presentation />} />
        <Route path="/story/branching" element={<BranchingStory />} />
        <Route
          path="/demo/:projectId"
          element={
            <AnonymousProvider>
              <Project />
            </AnonymousProvider>
          }
        />

        <Route
          path="*"
          element={
            (import.meta.env.DEV || session || flow360Auth || hasE2EToken) ? (
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/flow360" element={<Flow360Browser />} />
                <Route
                  path="/project/:projectId/fix/:sessionId"
                  element={<FixSessionPage />}
                />
                <Route
                  path="/project/:projectId"
                  element={<Project />}
                />
                <Route path="/campaigns/new" element={<CampaignConfigurator />} />
                <Route path="/campaigns/:id" element={<CampaignView />} />
              </Routes>
            ) : (
              <SignIn />
            )
          }
        />
      </Routes>
    </Theme>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AppRoutes />
      </ThemeProvider>
    </QueryClientProvider>
  );
}
